import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AxiosError } from "axios";
import { Tooltip } from "antd";
import Highlighter from "react-highlight-words";
import { FaBullseye } from "react-icons/fa";

import { IGiveaway } from "../../interfaces";
import { EGiveawayStatus } from "../../enums";
import { EmitSuccessAlert } from "../../utils/EmitSuccessAlert";
import { EmitError } from "../../utils/EmitError";
import GiveawaysService from "../../services/http/services/GiveawaysService";
import useAuth from "../../hooks/useAuth";
import { poeValorComPontosEmMil } from "../../utils/Conversores";
import { removeAcento } from "../../utils/RemoveAcentos";
import GiveawayStatusTag from "./components/GiveawayStatusTag";
import { ThemeContext } from "../../contexts/ThemeContext";
import TableActionButton from "../../components/TableActionButton";
import { IActiveFilterItem } from "../../components/ActiveFiltersBar";
import useTableColumnFilter from "../../hooks/useTableColumnFilter";

function renderDateTimeCell(data: any, isDark: boolean) {
    if (!data) return <span style={{ color: isDark ? '#666' : '#94a3b8' }}>--</span>;

    const d = new Date(data);
    if (isNaN(d.getTime())) return <span style={{ color: isDark ? '#666' : '#94a3b8' }}>--</span>;

    const dateStr = d.toLocaleDateString('pt-BR', {
        timeZone: 'America/Sao_Paulo',
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
    });

    const timeStr = d.toLocaleTimeString('pt-BR', {
        timeZone: 'America/Sao_Paulo',
        hour: '2-digit',
        minute: '2-digit',
    });

    return (
        <div style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', lineHeight: 1.25 }}>
            <span style={{ color: isDark ? '#f0f0f0' : '#1e293b', fontSize: 12.5, fontWeight: 500, whiteSpace: 'nowrap' }}>
                {dateStr}
            </span>
            <span style={{ color: isDark ? '#8c8c8c' : '#64748b', fontSize: 11, fontWeight: 400, whiteSpace: 'nowrap', marginTop: 1 }}>
                {timeStr}
            </span>
        </div>
    );
}

export interface ITableData extends IGiveaway {
    key: number | undefined;
}

export interface IGiveawayFilters {
    description: string | null;
    status: string | null;
}

export type SortField =
    | 'description'
    | 'period_beginning'
    | 'period_ending'
    | 'date'
    | 'import_deadline'
    | 'total_user_participating'
    | 'total_tickets'
    | 'status'
    | null;

export default function useGiveaways() {
    const { theme } = useContext(ThemeContext);
    const isDark = theme === 'dark';
    const { getColumnSearchProps, getColumnRadioProps } = useTableColumnFilter();

    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [isLoadingDelete, setIsLoadingDelete] = useState<boolean>(false);
    const [isDeleteModalVisible, setIsDeleteModalVisible] = useState<boolean>(false);
    const [rawData, setRawData] = useState<Array<ITableData>>([]);
    const [giveawayBeingDeleted, setGiveawayBeingDeleted] = useState<ITableData | null>(null);
    const [pagination, setPagination] = useState({ current: 1, pageSize: 25 });

    const [filters, setFilters] = useState<IGiveawayFilters>({
        description: null,
        status: null,
    });

    const [sorter, setSorter] = useState<{ field: SortField; order: 'asc' | 'desc' | null }>({
        field: 'date',
        order: 'desc',
    });

    const [searchDescription, setSearchDescription] = useState('');

    const navigate = useNavigate();
    const { activeCity } = useAuth();

    const handleFilterChange = useCallback((key: keyof IGiveawayFilters, value: any) => {
        setFilters((prev) => ({ ...prev, [key]: value }));
        setPagination((prev) => ({ ...prev, current: 1 }));
    }, []);

    const handleClearAllFilters = useCallback(() => {
        setFilters({
            description: null,
            status: null,
        });
        setSearchDescription('');
        setPagination((prev) => ({ ...prev, current: 1 }));
    }, []);

    const stats = useMemo(() => {
        const total = rawData.length;
        const vigentes = rawData.filter(
            (g) =>
                g.is_valid &&
                (g.status === EGiveawayStatus.FORCE ||
                    g.status === EGiveawayStatus.IMPORT_PERIOD)
        ).length;
        const aguardando = rawData.filter(
            (g) =>
                g.is_valid &&
                (g.status === EGiveawayStatus.WAITING_RAFFLE ||
                    g.status === EGiveawayStatus.WAITING_NEW_RAFFLE ||
                    g.status === EGiveawayStatus.UNDER_VERIFICATION)
        ).length;
        const finalizados = rawData.filter(
            (g) => g.is_valid && g.status === EGiveawayStatus.FINISHED
        ).length;

        return { total, vigentes, aguardando, finalizados };
    }, [rawData]);

    const filteredData = useMemo(() => {
        let result = rawData;

        if (filters.description) {
            const query = removeAcento(filters.description.trim().toLowerCase());
            result = result.filter((item) =>
                item.description ? removeAcento(item.description.toLowerCase()).includes(query) : false
            );
        }

        if (filters.status) {
            if (filters.status === 'Inválido') {
                result = result.filter((item) => !item.is_valid);
            } else {
                result = result.filter((item) => item.status === filters.status && item.is_valid);
            }
        }

        if (sorter.field && sorter.order) {
            result = [...result].sort((a, b) => {
                if (sorter.field === 'description') {
                    const comp = (a.description || '').localeCompare(b.description || '');
                    return sorter.order === 'asc' ? comp : -comp;
                }
                if (sorter.field === 'period_beginning') {
                    const valA = new Date(a.period_beginning || 0).getTime();
                    const valB = new Date(b.period_beginning || 0).getTime();
                    return sorter.order === 'asc' ? valA - valB : valB - valA;
                }
                if (sorter.field === 'period_ending') {
                    const valA = new Date(a.period_ending || 0).getTime();
                    const valB = new Date(b.period_ending || 0).getTime();
                    return sorter.order === 'asc' ? valA - valB : valB - valA;
                }
                if (sorter.field === 'date') {
                    const valA = new Date(a.date || 0).getTime();
                    const valB = new Date(b.date || 0).getTime();
                    return sorter.order === 'asc' ? valA - valB : valB - valA;
                }
                if (sorter.field === 'import_deadline') {
                    const valA = new Date(a.import_deadline || 0).getTime();
                    const valB = new Date(b.import_deadline || 0).getTime();
                    return sorter.order === 'asc' ? valA - valB : valB - valA;
                }
                if (sorter.field === 'total_user_participating') {
                    const valA = a.total_user_participating || 0;
                    const valB = b.total_user_participating || 0;
                    return sorter.order === 'asc' ? valA - valB : valB - valA;
                }
                if (sorter.field === 'total_tickets') {
                    const valA = a.total_tickets || 0;
                    const valB = b.total_tickets || 0;
                    return sorter.order === 'asc' ? valA - valB : valB - valA;
                }
                if (sorter.field === 'status') {
                    const comp = (a.status || '').localeCompare(b.status || '');
                    return sorter.order === 'asc' ? comp : -comp;
                }
                return 0;
            });
        }

        return result;
    }, [rawData, filters, sorter]);

    const activeFilterItems = useMemo<IActiveFilterItem[]>(() => {
        const items: IActiveFilterItem[] = [];

        if (filters.description) {
            items.push({
                key: 'description',
                label: 'Descrição',
                value: filters.description,
                onRemove: () => {
                    setFilters((prev) => ({ ...prev, description: null }));
                    setSearchDescription('');
                },
            });
        }

        if (filters.status) {
            items.push({
                key: 'status',
                label: 'Status',
                value: filters.status,
                onRemove: () => setFilters((prev) => ({ ...prev, status: null })),
            });
        }

        return items;
    }, [filters]);

    const totalGiveawaysCount = filteredData.length;

    const tableData = useMemo(() => {
        const startIndex = (pagination.current - 1) * pagination.pageSize;
        const endIndex = startIndex + pagination.pageSize;
        return filteredData.slice(startIndex, endIndex);
    }, [filteredData, pagination]);

    const getSortOrder = useCallback(
        (field: SortField) => {
            if (sorter.field === field) {
                return sorter.order === 'asc'
                    ? ('ascend' as const)
                    : sorter.order === 'desc'
                    ? ('descend' as const)
                    : null;
            }
            return null;
        },
        [sorter]
    );

    const handleTableChange = (
        _pagination: any,
        _filters: any,
        sorterResult: any
    ) => {
        if (sorterResult && sorterResult.field) {
            const field = sorterResult.field as SortField;
            const order =
                sorterResult.order === 'ascend'
                    ? 'asc'
                    : sorterResult.order === 'descend'
                    ? 'desc'
                    : null;
            setSorter({ field, order });
        } else {
            setSorter({ field: 'date', order: 'desc' });
        }
    };

    const handlePaginationChange = useCallback((page: number, pageSize: number) => {
        setPagination({ current: page, pageSize });
    }, []);

    const canHoldGiveaway = useCallback(
        (id: number) => {
            const giveaway = rawData.find((g) => g.id === id);
            const statusThatCanAccess = [
                'Aguardando sorteio',
                'Aguardando novo sorteio',
                'Em verificação',
                'Finalizado',
            ];

            if (!giveaway || !statusThatCanAccess.includes(giveaway.status) || !giveaway.is_valid) {
                return false;
            }

            return true;
        },
        [rawData]
    );

    const columns = [
        {
            title: 'Sorteio / Descrição',
            dataIndex: 'description',
            key: 'description',
            sorter: true,
            sortOrder: getSortOrder('description'),
            showSorterTooltip: false,
            width: 210,
            ...getColumnSearchProps({
                title: 'Buscar Sorteio',
                placeholder: 'Digite a descrição...',
                value: searchDescription,
                onChange: setSearchDescription,
                onSearch: (val) => handleFilterChange('description', val),
                isActive: !!filters.description,
            }),
            render: (text: string, record: ITableData) => (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <span style={{ fontWeight: 600, color: isDark ? '#f0f0f0' : '#1e293b', fontSize: 13, lineHeight: '1.3' }}>
                        {filters.description ? (
                            <Highlighter
                                highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
                                searchWords={[filters.description]}
                                autoEscape
                                textToHighlight={text || '--'}
                            />
                        ) : (
                            text || '--'
                        )}
                    </span>
                    {record.type ? (
                        <span style={{ color: isDark ? '#8c8c8c' : '#64748b', fontSize: 11.5, fontWeight: 500 }}>
                            {record.type}
                        </span>
                    ) : null}
                </div>
            ),
        },
        {
            title: 'Início',
            dataIndex: 'period_beginning',
            key: 'period_beginning',
            align: 'center' as const,
            width: 110,
            sorter: true,
            sortOrder: getSortOrder('period_beginning'),
            showSorterTooltip: false,
            render: (data: any) => renderDateTimeCell(data, isDark),
        },
        {
            title: 'Fim',
            dataIndex: 'period_ending',
            key: 'period_ending',
            align: 'center' as const,
            width: 110,
            sorter: true,
            sortOrder: getSortOrder('period_ending'),
            showSorterTooltip: false,
            render: (data: any) => renderDateTimeCell(data, isDark),
        },
        {
            title: 'Data do Sorteio',
            dataIndex: 'date',
            key: 'date',
            align: 'center' as const,
            width: 160,
            sorter: true,
            sortOrder: getSortOrder('date'),
            showSorterTooltip: false,
            render: (data: any) => renderDateTimeCell(data, isDark),
        },
        {
            title: 'Limite Importação',
            dataIndex: 'import_deadline',
            key: 'import_deadline',
            align: 'center' as const,
            width: 170,
            sorter: true,
            sortOrder: getSortOrder('import_deadline'),
            showSorterTooltip: false,
            render: (data: any) => renderDateTimeCell(data, isDark),
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            align: 'center' as const,
            width: 140,
            sorter: true,
            sortOrder: getSortOrder('status'),
            showSorterTooltip: false,
            ...getColumnRadioProps({
                title: 'Filtrar por Status',
                value: filters.status || 'ALL',
                onChange: (val) => handleFilterChange('status', val === 'ALL' ? null : val),
                onClear: () => handleFilterChange('status', null),
                isActive: !!filters.status,
                options: [
                    { label: 'Todos', value: 'ALL' },
                    { label: 'Vigente', value: 'Vigente' },
                    { label: 'Período de Importação', value: 'Periodo de importação' },
                    { label: 'Aguardando Sorteio', value: 'Aguardando sorteio' },
                    { label: 'Aguardando Novo Sorteio', value: 'Aguardando novo sorteio' },
                    { label: 'Em Verificação', value: 'Em verificação' },
                    { label: 'Finalizado', value: 'Finalizado' },
                    { label: 'Em Espera', value: 'Em espera' },
                    { label: 'Inválido', value: 'Inválido' },
                ],
            }),
            render: (_: any, record: ITableData) => (
                <GiveawayStatusTag status={record.status} isValid={record.is_valid} key={`tag-${record.id}`} />
            ),
        },
        {
            title: 'Participantes',
            dataIndex: 'total_user_participating',
            key: 'total_user_participating',
            align: 'center' as const,
            width: 135,
            sorter: true,
            sortOrder: getSortOrder('total_user_participating'),
            showSorterTooltip: false,
            render: (_: any, { total_user_participating, mode }: ITableData) =>
                mode === 'normal' || total_user_participating ? (
                    <Tooltip color="#1677ff" title="Total de participantes cadastrados">
                        <span style={{ fontWeight: 600, color: '#1677ff', fontSize: 13.5, whiteSpace: 'nowrap' }}>
                            {poeValorComPontosEmMil(total_user_participating ?? 0)}
                        </span>
                    </Tooltip>
                ) : (
                    <span style={{ color: isDark ? '#666' : '#94a3b8' }}>--</span>
                ),
        },
        {
            title: 'Bilhetes',
            dataIndex: 'total_tickets',
            key: 'total_tickets',
            align: 'center' as const,
            width: 120,
            sorter: true,
            sortOrder: getSortOrder('total_tickets'),
            showSorterTooltip: false,
            render: (_: any, { total_tickets, mode }: ITableData) =>
                mode === 'normal' || total_tickets ? (
                    <Tooltip color="#722ed1" title="Total de bilhetes concorrendo">
                        <span style={{ fontWeight: 600, color: '#722ed1', fontSize: 13.5, whiteSpace: 'nowrap' }}>
                            {poeValorComPontosEmMil(total_tickets ?? 0)}
                        </span>
                    </Tooltip>
                ) : (
                    <span style={{ color: isDark ? '#666' : '#94a3b8' }}>--</span>
                ),
        },
        {
            title: 'Ações',
            key: 'actions',
            align: 'center' as const,
            width: 140,
            fixed: 'right' as const,
            render: (_: any, record: ITableData) => {
                const canHold = canHoldGiveaway(record.id);
                return (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                        <TableActionButton
                            action="custom"
                            tooltip={canHold ? "Realizar / Processar sorteio" : "Sorteio não disponível para realização neste status"}
                            icon={<FaBullseye style={{ fontSize: 13, color: canHold ? '#52c41a' : (isDark ? 'rgba(255,255,255,0.25)' : '#94a3b8') }} />}
                            color="#52c41a"
                            bgColor={isDark ? 'rgba(82, 196, 26, 0.15)' : '#f6ffed'}
                            disabled={!canHold}
                            onClick={() => handleClickHoldGiveaway(record.id)}
                        />
                        <TableActionButton
                            action="view"
                            tooltip="Visualizar detalhes do sorteio"
                            onClick={() => handleClickViewGiveawayDetails(record.id)}
                        />
                        <TableActionButton
                            action="edit"
                            tooltip="Editar sorteio"
                            onClick={() => handleClickEditGiveaway(record.id)}
                        />
                        <TableActionButton
                            action="delete"
                            tooltip="Excluir sorteio"
                            onClick={() => handleDeleteGiveaway(record.id)}
                        />
                    </div>
                );
            },
        },
    ];

    const setTableDataFromRequest = useCallback((data: Array<IGiveaway>) => {
        const newData = data.map((giveaway) => ({ ...giveaway, key: giveaway.id }));
        setRawData(newData);
    }, []);

    useEffect(() => {
        const controller = new AbortController();

        async function loadGiveaways() {
            try {
                setIsLoading(true);

                const giveawaysList = await GiveawaysService.listGiveaways({
                    cityId: activeCity?.id!,
                    signal: controller.signal,
                });

                setTableDataFromRequest(giveawaysList);
                setIsLoading(false);
            } catch (err) {
                if (err instanceof AxiosError && err.name === 'CanceledError') {
                    return;
                }

                EmitError(err, true, 'giveaways_loadError');
                setIsLoading(false);
            }
        }

        loadGiveaways();

        return () => {
            controller.abort();
        };
    }, [setTableDataFromRequest, activeCity]);

    const handleDeleteGiveaway = useCallback(
        (id: number) => {
            setIsDeleteModalVisible(true);
            setGiveawayBeingDeleted(rawData.find((element) => element.id === id) || null);
        },
        [rawData]
    );

    const handleConfirmDeleteGiveaway = useCallback(async () => {
        try {
            setIsLoadingDelete(true);

            await GiveawaysService.deleteGiveaway(giveawayBeingDeleted?.id!);

            setRawData((prevState) => prevState.filter((element) => element.id !== giveawayBeingDeleted?.id));
            setIsDeleteModalVisible(false);
            setGiveawayBeingDeleted(null);
            setIsLoadingDelete(false);
            EmitSuccessAlert('Sorteio deletado com sucesso!');
        } catch (err) {
            EmitError(err, true, 'giveaways_deleteError');
            setIsLoadingDelete(false);
        }
    }, [giveawayBeingDeleted]);

    const handleCancelDeleteGiveaway = useCallback(() => {
        setIsDeleteModalVisible(false);
        setGiveawayBeingDeleted(null);
    }, []);

    const handleClickCreateNewGiveaway = useCallback(() => {
        navigate('/sorteios/geral/criar');
    }, [navigate]);

    const handleClickEditGiveaway = useCallback(
        (id: number) => {
            navigate(`/sorteios/geral/editar/${id}`);
        },
        [navigate]
    );

    const handleClickHoldGiveaway = useCallback(
        (id: number) => {
            const giveaway = rawData.find((g) => g.id === id);

            if (!giveaway) {
                return;
            }

            if (giveaway.type === 'Loteria Federal') {
                navigate(`/sorteios/geral/${id}/processar-sorteio`);
                return;
            }

            navigate(`/sorteios/geral/${id}/realizar-sorteio`);
        },
        [navigate, rawData]
    );

    const handleClickViewGiveawayDetails = useCallback(
        (id: number) => {
            navigate(`/sorteios/geral/visualizar/${id}`);
        },
        [navigate]
    );

    return {
        isLoading,
        isDeleteModalVisible,
        isLoadingDelete,
        handleClickCreateNewGiveaway,
        handleConfirmDeleteGiveaway,
        handleCancelDeleteGiveaway,
        handleClickViewGiveawayDetails,
        columns,
        tableData,
        totalGiveawaysCount,
        stats,
        pagination,
        activeFilterItems,
        handleTableChange,
        handlePaginationChange,
        handleClearAllFilters,
        giveawayBeingDeleted,
        navigate,
    };
}
